import React, { useState, useEffect } from "react";
import {
    Building2,
    Search,
    Filter,
    MoreHorizontal,
    Mail,
    AlertCircle,
    Edit3,
    Trash2,
    CheckCircle,
    X,
    MapPin,
    CreditCard,
    Globe,
    Loader2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "../../firebase";
import { collection, onSnapshot, doc, updateDoc, deleteDoc, query, orderBy } from "firebase/firestore";
import useCurrentUser from "../../hooks/useCurrentUser";

export default function CompanyManagement() {
    const { role } = useCurrentUser();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Modal state
    const [managingCompany, setManagingCompany] = useState(null);
    const [manageType, setManageType] = useState(null); // 'edit' or 'delete'
    const [editData, setEditData] = useState({});
    const [actionLoading, setActionLoading] = useState(false);
    const [actionStatus, setActionStatus] = useState({ type: null, message: "" });

    // Ensure only admins can access
    if (role && role !== "admin" && role !== "super-admin") {
        return (
            <div className="flex flex-col items-center justify-center p-12 h-[80vh]">
                <AlertCircle className="text-red-500 mb-4" size={48} />
                <h2 className="text-2xl font-bold text-navy-900 font-serif">Access Denied</h2>
                <p className="text-slate-500 mt-2">You do not have administrative privileges to view this page.</p>
            </div>
        );
    }

    useEffect(() => {
        const q = query(collection(db, "companies"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const comps = [];
            snapshot.forEach((doc) => {
                comps.push({ id: doc.id, ...doc.data() });
            });
            // Sort client-side by name 
            comps.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            setCompanies(comps);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching companies:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const filteredCompanies = companies.filter(company =>
        (company.name && company.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (company.contactEmail && company.contactEmail.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const openEditModal = (company) => {
        setManagingCompany(company);
        setEditData({
            name: company.name || "",
            contactEmail: company.contactEmail || "",
            address: company.address || "",
            country: company.country || "",
            paymentPlan: company.paymentPlan || "starter",
            status: company.status || "active"
        });
        setManageType("edit");
        setActionStatus({ type: null, message: "" });
    };

    const openDeleteModal = (company) => {
        setManagingCompany(company);
        setManageType("delete");
        setActionStatus({ type: null, message: "" });
    };

    const handleUpdateCompany = async () => {
        setActionLoading(true);
        setActionStatus({ type: null, message: "" });
        try {
            const companyRef = doc(db, "companies", managingCompany.id);
            await updateDoc(companyRef, editData);
            setActionStatus({ type: "success", message: "Company updated successfully!" });
            setTimeout(() => {
                setManagingCompany(null);
            }, 1500);
        } catch (error) {
            console.error("Error updating company:", error);
            setActionStatus({ type: "error", message: "Failed to update company. Please try again." });
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteCompany = async (hardDelete = false) => {
        setActionLoading(true);
        setActionStatus({ type: null, message: "" });
        try {
            const companyRef = doc(db, "companies", managingCompany.id);
            if (hardDelete) {
                await deleteDoc(companyRef);
                setActionStatus({ type: "success", message: "Company completely deleted." });
                // Note: Deleting related data should ideally be done natively via a backend trigger or cloud function.
            } else {
                await updateDoc(companyRef, { status: "deleted" });
                setActionStatus({ type: "success", message: "Company status set to deleted (Soft Delete)." });
            }
            setTimeout(() => {
                setManagingCompany(null);
            }, 1500);
        } catch (error) {
            console.error("Error deleting company:", error);
            setActionStatus({ type: "error", message: "Failed to delete company. Please try again." });
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return "N/A";
        // Handle Firestore timestamps vs string dates
        if (dateValue.toDate) return dateValue.toDate().toLocaleDateString();
        return new Date(dateValue).toLocaleDateString();
    };

    return (
        <div className="max-w-7xl mx-auto py-8 px-4">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-navy-900 font-serif">Companies</h1>
                    <p className="text-slate-500 mt-1">Manage all registered client companies, subscription plans, and status.</p>
                </div>
            </div>

            {/* Filter Toolbar */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="relative flex-1 md:max-w-md w-full">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                    <input
                        type="text"
                        placeholder="Search by company name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-navy-900/10"
                    />
                </div>
                <div className="flex gap-2">
                    <span className="text-xs font-bold text-slate-500 bg-slate-100 px-3 py-2 rounded-lg">
                        Total Companies: {companies.length}
                    </span>
                </div>
            </div>

            {/* Companies Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left whitespace-nowrap">
                        <thead className="text-[10px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/50">
                            <tr>
                                <th className="px-6 py-4">Company</th>
                                <th className="px-6 py-4">Location</th>
                                <th className="px-6 py-4">Payment Plan</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Created</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center text-slate-500">
                                        <Loader2 className="animate-spin inline-block text-navy-900" size={24} />
                                    </td>
                                </tr>
                            ) : filteredCompanies.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-8 text-center text-slate-500 font-medium">
                                        No companies found matching your search.
                                    </td>
                                </tr>
                            ) : (
                                filteredCompanies.map((company) => (
                                    <tr key={company.id} className="hover:bg-slate-50 transition-colors group">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center font-bold text-slate-500">
                                                    {(company.name && company.name[0]) ? company.name[0].toUpperCase() : <Building2 size={18} />}
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-navy-900">{company.name || "N/A"}</span>
                                                    <span className="text-xs text-slate-400 flex items-center gap-1">
                                                        {company.contactEmail || "No Email"}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="text-sm font-medium text-navy-900">{company.country || "N/A"}</span>
                                                <span className="text-xs text-slate-400 truncate max-w-[150px]">{company.address || ""}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${company.paymentPlan === 'enterprise' ? 'bg-purple-100 text-purple-700' :
                                                    company.paymentPlan === 'growth' ? 'bg-blue-100 text-blue-700' :
                                                        company.paymentPlan === 'custom' ? 'bg-amber-100 text-amber-700' :
                                                            'bg-slate-100 text-slate-700'
                                                }`}>
                                                {company.paymentPlan || "starter"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${company.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                                                    company.status === 'deleted' ? 'bg-red-100 text-red-700' :
                                                        'bg-amber-100 text-amber-700'
                                                }`}>
                                                {company.status || "active"}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-500">
                                            {formatDate(company.createdAt || company.created_at)}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => openEditModal(company)}
                                                    className="p-2 hover:bg-slate-200 rounded-lg text-slate-500 hover:text-navy-900 transition-all"
                                                    title="Edit Company"
                                                >
                                                    <Edit3 size={16} />
                                                </button>
                                                <button
                                                    onClick={() => openDeleteModal(company)}
                                                    className="p-2 hover:bg-red-50 rounded-lg text-slate-500 hover:text-red-600 transition-all"
                                                    title="Delete Company"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Edit / Delete Modal */}
            <AnimatePresence>
                {managingCompany && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/60 backdrop-blur-sm"
                    >
                        <motion.div
                            initial={{ y: 50, opacity: 0, scale: 0.95 }}
                            animate={{ y: 0, opacity: 1, scale: 1 }}
                            exit={{ y: 20, opacity: 0, scale: 0.95 }}
                            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]"
                        >
                            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                                <h3 className="font-bold text-navy-900 text-xl font-serif">
                                    {manageType === "edit" ? "Edit Company Details" : "Delete Company"}
                                </h3>
                                <button
                                    onClick={() => setManagingCompany(null)}
                                    className="p-2 text-slate-400 hover:bg-slate-200 hover:text-navy-900 rounded-full transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="p-6 overflow-y-auto">
                                {/* Success/Error State message */}
                                <AnimatePresence mode="wait">
                                    {actionStatus.type && (
                                        <motion.div
                                            key="statusMsg"
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: "auto" }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className={`p-4 rounded-xl flex items-start gap-3 mb-6 ${actionStatus.type === "success" ? "bg-emerald-50 text-emerald-700 border border-emerald-100" : "bg-red-50 text-red-700 border border-red-100"
                                                }`}
                                        >
                                            {actionStatus.type === "success" ? <CheckCircle size={18} className="shrink-0" /> : <AlertCircle size={18} className="shrink-0" />}
                                            <p className="text-sm font-bold">{actionStatus.message}</p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {manageType === "edit" && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="col-span-2">
                                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Company Name</label>
                                                <div className="relative">
                                                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                                    <input type="text" value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-navy-900 focus:ring-1 focus:ring-navy-900 outline-none transition-all" />
                                                </div>
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Contact Email</label>
                                                <div className="relative">
                                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                                    <input type="email" value={editData.contactEmail} onChange={(e) => setEditData({ ...editData, contactEmail: e.target.value })} className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-navy-900 focus:ring-1 focus:ring-navy-900 outline-none transition-all" />
                                                </div>
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Address</label>
                                                <div className="relative">
                                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                                    <input type="text" value={editData.address} onChange={(e) => setEditData({ ...editData, address: e.target.value })} className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-navy-900 focus:ring-1 focus:ring-navy-900 outline-none transition-all" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Country</label>
                                                <div className="relative">
                                                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                                    <input type="text" value={editData.country} onChange={(e) => setEditData({ ...editData, country: e.target.value })} className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-navy-900 focus:ring-1 focus:ring-navy-900 outline-none transition-all" />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Status</label>
                                                <select value={editData.status} onChange={(e) => setEditData({ ...editData, status: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-navy-900 focus:ring-1 focus:ring-navy-900 outline-none transition-all">
                                                    <option value="active">Active</option>
                                                    <option value="suspended">Suspended</option>
                                                    <option value="deleted">Deleted</option>
                                                </select>
                                            </div>
                                            <div className="col-span-2">
                                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Payment Plan</label>
                                                <div className="relative">
                                                    <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                                                    <select value={editData.paymentPlan} onChange={(e) => setEditData({ ...editData, paymentPlan: e.target.value })} className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-navy-900 focus:ring-1 focus:ring-navy-900 outline-none transition-all">
                                                        <option value="starter">Starter</option>
                                                        <option value="growth">Growth</option>
                                                        <option value="enterprise">Enterprise</option>
                                                        <option value="custom">Custom</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>

                                        <button
                                            onClick={handleUpdateCompany}
                                            disabled={actionLoading}
                                            className="w-full mt-4 py-3 bg-navy-900 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-navy-800 transition-all shadow-lg shadow-navy-900/20 disabled:opacity-50"
                                        >
                                            {actionLoading ? <Loader2 className="animate-spin" size={18} /> : null} Save Changes
                                        </button>
                                    </div>
                                )}

                                {manageType === "delete" && (
                                    <div className="space-y-4 text-center">
                                        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <AlertCircle size={32} />
                                        </div>
                                        <h4 className="font-bold text-navy-900 text-lg">Delete "{managingCompany.name}"?</h4>
                                        <p className="text-sm text-slate-500 mb-6">
                                            This action cannot be fully undone. You can either soft-delete (recommended) or completely wipe this company from the database.
                                        </p>

                                        <div className="flex flex-col gap-3">
                                            <button
                                                onClick={() => handleDeleteCompany(false)}
                                                disabled={actionLoading}
                                                className="w-full py-3 bg-red-50 text-red-600 border border-red-200 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-red-100 transition-all disabled:opacity-50"
                                            >
                                                {actionLoading ? <Loader2 className="animate-spin" size={18} /> : null} Soft Delete (Set Status to Deleted)
                                            </button>

                                            <button
                                                onClick={() => handleDeleteCompany(true)}
                                                disabled={actionLoading}
                                                className="w-full py-3 bg-red-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-red-700 transition-all shadow-lg shadow-red-600/20 disabled:opacity-50"
                                            >
                                                {actionLoading ? <Loader2 className="animate-spin" size={18} /> : null} Hard Delete (Wipe Data)
                                            </button>
                                            <button
                                                onClick={() => setManagingCompany(null)}
                                                disabled={actionLoading}
                                                className="w-full py-3 bg-slate-100 text-slate-600 rounded-xl font-bold hover:bg-slate-200 transition-all disabled:opacity-50"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
